"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { ChevronDown, Menu, X } from "lucide-react";

import { LandingRequestDemoModal } from "@/components/landing/layouts/lumina/LandingRequestDemoModal";
import {
  openRequestDemo,
  RequestAccessLink,
} from "@/components/marketing/shared/MarketingCTA";
import { NoScriptReveal } from "@/components/marketing/shared/NoScriptReveal";
import { useMarketingNavScroll } from "@/components/marketing/shared/useMarketingNavScroll";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  SECURITY_ADVISOR_CONTROLS,
  SECURITY_COMPLIANCE_FRAMEWORKS,
  SECURITY_COMPLIANCE_ITEMS,
  SECURITY_INFO_SECTIONS,
  SECURITY_RECORD_ITEMS,
  SECURITY_SEC_DEADLINES,
  SECURITY_SEC_QA,
  SECURITY_VDP_RESPONSE_COMMITMENT,
  SECURITY_VDP_REWARDS_CRITERIA,
  SECURITY_VDP_RULES,
  SECURITY_VDP_SCOPE_IN,
  SECURITY_VDP_SCOPE_OUT,
} from "../data";
import type { SecurityInfoSection } from "../types";
import { securityPageStyles } from "./securityPageStyles";
import { SUPPORT_EMAIL } from "@/lib/contact";
import { TRUST_PORTAL_URL } from "@/lib/contact";

const TRUST_PORTAL_HREF = TRUST_PORTAL_URL;

const tocSections = [
  { id: "sec-overview", label: "Overview" },
  { id: "sec-infra", label: "Infrastructure & network" },
  { id: "sec-data", label: "Data protection" },
  { id: "sec-appsec", label: "Application security" },
  { id: "sec-cicd", label: "CI/CD & supply chain" },
  { id: "sec-ops", label: "Monitoring & operations" },
  { id: "sec-governance", label: "Compliance & governance" },
  { id: "sec-regulatory", label: "Regulatory alignment" },
  { id: "sec-vdp", label: "Vulnerability disclosure" },
  { id: "sec-faq", label: "FAQ" },
] as const;

type TocSectionId = (typeof tocSections)[number]["id"];

type SecurityRevealStyle = CSSProperties & {
  "--security-reveal-delay": string;
};

function securityRevealStyle(delay: number): SecurityRevealStyle {
  return { "--security-reveal-delay": `${delay}ms` };
}

const securityDomainSections = SECURITY_INFO_SECTIONS;

function useRevealEffects() {
  useEffect(() => {
    const revealItems = Array.from(
      document.querySelectorAll<HTMLElement>(
        ".zmk-security-root [data-sreveal]",
      ),
    );

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      revealItems.forEach((item) => item.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" },
    );

    revealItems.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);
}

function SecurityScrollProgress() {
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;

    let frame = 0;

    const update = () => {
      const maxScroll =
        document.documentElement.scrollHeight - window.innerHeight;
      const nextProgress =
        maxScroll > 0 ? Math.min(1, window.scrollY / maxScroll) : 0;
      bar.style.transform = `scaleX(${nextProgress})`;
      frame = 0;
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return <div ref={barRef} className="zmk-sec-progress" aria-hidden />;
}

function mobileTocOffset() {
  const navHidden = document
    .querySelector(".zmk-security-root")
    ?.classList.contains("is-nav-hidden");
  return window.matchMedia("(max-width: 767px)").matches
    ? navHidden
      ? 64
      : 122
    : 130;
}

function useActiveSection() {
  const [activeId, setActiveId] = useState<TocSectionId>(tocSections[0].id);

  useEffect(() => {
    let frame = 0;

    const update = () => {
      const offset = mobileTocOffset();
      let current: TocSectionId = tocSections[0].id;

      for (const section of tocSections) {
        const node = document.getElementById(section.id);
        if (!node) continue;
        if (node.getBoundingClientRect().top - offset <= 1) {
          current = section.id;
        }
      }

      setActiveId((previous) => (previous === current ? previous : current));
      frame = 0;
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return activeId;
}

function SecurityNav({
  scrolled,
  hidden,
  setHidden,
}: {
  scrolled: boolean;
  hidden: boolean;
  setHidden: (next: boolean) => void;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleRequestAccess = () => {
    setMobileOpen(false);
    openRequestDemo("security_nav");
  };

  return (
    <>
      <header
        className={cn(
          "zmk-sec-nav",
          scrolled && "is-scrolled",
          hidden && !mobileOpen && "is-hidden",
        )}
        onFocus={() => setHidden(false)}
      >
        <nav className="zmk-sec-nav__inner" aria-label="Main navigation">
          <Link className="zmk-sec-nav__brand" href="/" aria-label="Zom home">
            <Image
              src="/images/zom-marketing/logo-white-pictorial.png"
              alt="Zom"
              width={64}
              height={64}
              priority
            />
          </Link>
          <div className="zmk-sec-nav__links">
            <Link href="/about">About</Link>
            <Link href="/security">Security</Link>
            <Button
              type="button"
              variant="ghost"
              className="zmk-sec-nav__request"
              onClick={handleRequestAccess}
            >
              Request access
            </Button>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="zmk-sec-nav__menu"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((current) => !current)}
          >
            {mobileOpen ? <X aria-hidden /> : <Menu aria-hidden />}
          </Button>
        </nav>
      </header>

      {mobileOpen ? (
        <div className="zmk-sec-mobile-menu is-open">
          <Link href="/about" onClick={() => setMobileOpen(false)}>
            About
          </Link>
          <Link href="/security" onClick={() => setMobileOpen(false)}>
            Security
          </Link>
          <Button
            type="button"
            className="zmk-sec-button"
            onClick={handleRequestAccess}
          >
            Request access
          </Button>
        </div>
      ) : null}
    </>
  );
}

function TrustPortalButton({
  variant = "primary",
  label = "View our SOC 2 Trust Portal",
}: {
  variant?: "primary" | "outline";
  label?: string;
}) {
  return (
    <Button
      asChild
      className={
        variant === "primary" ? "zmk-sec-button" : "zmk-sec-outline-button"
      }
    >
      <Link href={TRUST_PORTAL_HREF} target="_blank" rel="noopener noreferrer">
        {label}
        <span aria-hidden>→</span>
      </Link>
    </Button>
  );
}

function HeroSection() {
  return (
    <section className="zmk-sec-hero">
      <div className="zmk-sec-hero__inner">
        <p className="zmk-sec-mono zmk-sec-kicker" data-sreveal>
          Security
        </p>
        <h1 className="zmk-sec-display" data-sreveal>
          Enterprise-grade security and controls.
        </h1>
        <p className="zmk-sec-lede" data-sreveal>
          Where available, Zom shows supporting source information for review.
          Client data sent to the model provider is processed under
          zero-data-retention terms and is not used to train third-party models.
        </p>
        <div className="zmk-sec-actions" data-sreveal>
          <TrustPortalButton />
          <Button asChild className="zmk-sec-outline-button">
            <Link href="#sec-vdp">
              Report a Vulnerability
              <span aria-hidden>→</span>
            </Link>
          </Button>
        </div>
        <div className="zmk-sec-hero-badges" data-sreveal>
          <span>AWS Infrastructure</span>
          <span>Encrypted at Rest</span>
          <span>Encrypted in Transit</span>
          <span>Secrets Management</span>
        </div>
      </div>
    </section>
  );
}

function OverviewSection() {
  return (
    <section id="sec-overview" className="zmk-sec-overview" data-sreveal>
      <h2 className="zmk-sec-document-kicker">Overview</h2>
      <p>
        Zom runs on AWS with a defense-in-depth architecture: workloads run in
        isolated private subnets with no public IPs, data is encrypted at rest
        and in transit, secrets are managed through AWS Secrets Manager and KMS,
        and access follows least-privilege, role-based principles. The platform
        is independently audited against the AICPA SOC 2 Type II framework.
        Everything on this page exists so your compliance team can verify each
        of those claims.
      </p>
    </section>
  );
}

function SecurityDomainSection({ section }: { section: SecurityInfoSection }) {
  return (
    <section id={section.key} className="zmk-sec-domain">
      <h2 className="zmk-sec-document-kicker" data-sreveal>
        {section.eyebrow}
      </h2>
      <p
        className="zmk-sec-domain__description [transition-delay:40ms]"
        data-sreveal
      >
        {section.description}
      </p>
      <div className="zmk-sec-card-grid">
        {section.items.map((item, index) => (
          <article
            className="zmk-sec-card [transition-delay:var(--security-reveal-delay)]"
            data-sreveal
            key={item.title}
            style={securityRevealStyle(50 + index * 30)}
          >
            <span className="zmk-sec-card__num">
              {String(index + 1).padStart(2, "0")}
            </span>
            <h3>{item.title}</h3>
            <p>{item.desc}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function GovernanceSection() {
  return (
    <section id="sec-governance" className="zmk-sec-domain">
      <h2 className="zmk-sec-document-kicker" data-sreveal>
        Compliance &amp; governance
      </h2>
      <p
        className="zmk-sec-domain__description [transition-delay:40ms]"
        data-sreveal
      >
        Our security controls are designed with compliance in mind, following
        defense-in-depth and least privilege principles.
      </p>
      <article
        className="zmk-sec-governance-trust [transition-delay:60ms]"
        data-sreveal
      >
        <h3>SOC 2 trust</h3>
        <p>
          Independently audited against the AICPA SOC 2 Type II framework.
          Review the current report and control details in our Trust Portal.
        </p>
        <TrustPortalButton />
      </article>
      <div className="zmk-sec-governance-grid">
        {SECURITY_COMPLIANCE_ITEMS.map((item, index) => (
          <article
            className="zmk-sec-governance-card [transition-delay:var(--security-reveal-delay)]"
            data-sreveal
            key={item.title}
            style={securityRevealStyle(90 + index * 30)}
          >
            <span className="zmk-sec-card__num">{item.num}</span>
            <h3>{item.title}</h3>
            <p>{item.desc}</p>
          </article>
        ))}
      </div>
      <div className="zmk-sec-governance-group">
        <h3 data-sreveal>Advisor control</h3>
        <div className="zmk-sec-card-grid">
          {SECURITY_ADVISOR_CONTROLS.map((item, index) => (
            <article
              className="zmk-sec-card [transition-delay:var(--security-reveal-delay)]"
              data-sreveal
              key={item.title}
              style={securityRevealStyle(50 + index * 30)}
            >
              <span className="zmk-sec-card__num">
                {String(index + 1).padStart(2, "0")}
              </span>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </article>
          ))}
        </div>
      </div>
      <div className="zmk-sec-governance-group">
        <h3 data-sreveal>Records and reviewability</h3>
        <div className="zmk-sec-card-grid">
          {SECURITY_RECORD_ITEMS.map((item, index) => (
            <article
              className="zmk-sec-card [transition-delay:var(--security-reveal-delay)]"
              data-sreveal
              key={item.title}
              style={securityRevealStyle(50 + index * 30)}
            >
              <span className="zmk-sec-card__num">
                {String(index + 1).padStart(2, "0")}
              </span>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function RegulatorySection() {
  return (
    <section id="sec-regulatory" className="zmk-sec-regulatory">
      <h2 className="zmk-sec-document-kicker" data-sreveal>
        Regulatory alignment
      </h2>
      <div className="zmk-sec-regulatory__group">
        <h3 data-sreveal>What we build around</h3>
        <dl
          className="zmk-sec-regulatory__rows [transition-delay:80ms]"
          data-sreveal
        >
          {SECURITY_COMPLIANCE_FRAMEWORKS.map((item, index) => (
            <div className="zmk-sec-regulatory__row" key={item}>
              <dt>Reference {String(index + 1).padStart(2, "0")}</dt>
              <dd>{item}</dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="zmk-sec-regulatory__group">
        <h3 data-sreveal>Deadlines that matter</h3>
        <dl
          className="zmk-sec-regulatory__rows [transition-delay:80ms]"
          data-sreveal
        >
          {SECURITY_SEC_DEADLINES.map((item) => (
            <div className="zmk-sec-regulatory__row" key={item.when}>
              <dt>{item.when}</dt>
              <dd>{item.what}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

function BulletList({ items }: { items: readonly string[] }) {
  return (
    <ul className="zmk-sec-list">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

function VdpSection() {
  return (
    <section id="sec-vdp" className="zmk-sec-vdp">
      <h2 className="zmk-sec-document-kicker" data-sreveal>
        Vulnerability disclosure program (VDP)
      </h2>
      <p
        className="zmk-sec-vdp__description [transition-delay:40ms]"
        data-sreveal
      >
        Zom Technologies LLC welcomes responsible security research. If you
        believe you have found a security vulnerability in our products or
        services, please report it to us so we can investigate and remediate.
      </p>

      <div className="zmk-sec-vdp-grid">
        <article
          className="zmk-sec-vdp-card [transition-delay:60ms]"
          data-sreveal
        >
          <h3>Program overview</h3>
          <p>
            Zom operates a public Vulnerability Disclosure Program (VDP) with
            discretionary rewards, which is appropriate for our current stage
            and supports the vulnerability disclosure process firms expect
            during security review.
          </p>
        </article>
        <article
          className="zmk-sec-vdp-card [transition-delay:90ms]"
          data-sreveal
        >
          <h3>Report a vulnerability</h3>
          <span className="zmk-sec-vdp-card__label">Email</span>
          <Link
            className="zmk-sec-text-link zmk-sec-vdp-card__email"
            href={`mailto:${SUPPORT_EMAIL}`}
          >
            {SUPPORT_EMAIL}
          </Link>
          <p className="zmk-sec-vdp-card__fine">
            <strong>Please include:</strong> affected URL or component, steps to
            reproduce, impact, and any screenshots/logs.
          </p>
        </article>
      </div>

      <article
        className="zmk-sec-vdp-card zmk-sec-vdp-card--wide [transition-delay:120ms]"
        data-sreveal
      >
        <h3>Program rules</h3>
        <BulletList items={SECURITY_VDP_RULES} />
      </article>

      <div className="zmk-sec-vdp-grid zmk-sec-vdp-grid--scope">
        <article
          className="zmk-sec-vdp-card [transition-delay:60ms]"
          data-sreveal
        >
          <h3>In scope</h3>
          <BulletList items={SECURITY_VDP_SCOPE_IN} />
        </article>
        <article
          className="zmk-sec-vdp-card [transition-delay:90ms]"
          data-sreveal
        >
          <h3>Out of scope</h3>
          <BulletList items={SECURITY_VDP_SCOPE_OUT} />
        </article>
      </div>

      <div className="zmk-sec-vdp-grid">
        <article
          className="zmk-sec-vdp-card [transition-delay:60ms]"
          data-sreveal
        >
          <h3>Safe harbor</h3>
          <p>
            If you make a good-faith effort to follow this policy, avoid privacy
            violations, avoid service disruption, and report vulnerabilities
            promptly, Zom will not pursue legal action against you for your
            security research.
          </p>
        </article>
        <article
          className="zmk-sec-vdp-card [transition-delay:90ms]"
          data-sreveal
        >
          <h3>Our response commitment</h3>
          <div className="zmk-sec-response">
            {SECURITY_VDP_RESPONSE_COMMITMENT.map((item) => (
              <div className="zmk-sec-response__item" key={item.step}>
                <span className="zmk-sec-response__step">{item.step}</span>
                <span>{item.title}</span>
              </div>
            ))}
          </div>
        </article>
      </div>

      <div className="zmk-sec-vdp-grid zmk-sec-vdp-grid--scope">
        <article
          className="zmk-sec-vdp-card [transition-delay:60ms]"
          data-sreveal
        >
          <h3>Public disclosure</h3>
          <p>
            We request coordinated disclosure. Please do not publicly disclose
            vulnerabilities until we have confirmed a fix or agreed on a
            timeline.
          </p>
        </article>
        <article
          className="zmk-sec-vdp-card zmk-sec-vdp-card--rewards [transition-delay:90ms]"
          data-sreveal
        >
          <h3>Rewards &amp; program structure</h3>
          <h4>Program Type: Vulnerability Disclosure Program (VDP)</h4>
          <p className="zmk-sec-vdp-card__fine">
            This is a VDP, not a formal bug bounty program. There are no
            predefined reward tiers, minimum payouts, or platform listings, and
            no guaranteed monetary compensation for reports.
          </p>
          <h4>Discretionary rewards</h4>
          <p className="zmk-sec-vdp-card__fine">
            Zom may, at its discretion, offer non-monetary recognition or
            monetary rewards for high-impact vulnerability reports based on:
          </p>
          <BulletList items={SECURITY_VDP_REWARDS_CRITERIA} />
          <p className="zmk-sec-vdp-card__disclaimer">
            All rewards are discretionary, not guaranteed, and determined on a
            case-by-case basis.
          </p>
        </article>
      </div>
    </section>
  );
}

function FaqSection() {
  return (
    <section id="sec-faq" className="zmk-sec-faq-section">
      <h2 className="zmk-sec-document-kicker" data-sreveal>
        FAQ
      </h2>
      <div className="zmk-sec-faq [transition-delay:80ms]" data-sreveal>
        {SECURITY_SEC_QA.map((item) => (
          <details className="zmk-sec-faq__item" key={item.q}>
            <summary className="zmk-sec-faq__toggle">
              <span>{item.q}</span>
              <span className="zmk-sec-faq__icon" aria-hidden>
                +
              </span>
            </summary>
            <p className="zmk-sec-faq__answer">{item.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function ClosingNote() {
  return (
    <div className="zmk-sec-closing" data-sreveal>
      <p>
        Zom does not replace fiduciary judgment. It does not send client
        communications, execute trades, move money, or make final
        recommendations on its own. The advisor decides what reaches the client.
      </p>
      <Link
        className="zmk-sec-text-link"
        href={TRUST_PORTAL_HREF}
        target="_blank"
        rel="noopener noreferrer"
      >
        View our live trust center <span aria-hidden>→</span>
      </Link>
    </div>
  );
}

function SecurityToc() {
  const activeId = useActiveSection();
  const [open, setOpen] = useState(false);
  const listId = "zmk-sec-toc-list";
  const activeLabel =
    tocSections.find((section) => section.id === activeId)?.label ??
    tocSections[0].label;

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  return (
    <aside
      className={cn("zmk-sec-toc", open && "is-open")}
      aria-label="Security page sections"
    >
      <p className="zmk-sec-toc__title">Contents</p>
      <Button
        type="button"
        variant="ghost"
        className="zmk-sec-toc__trigger"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="zmk-sec-toc__trigger-label">{activeLabel}</span>
        <ChevronDown className="zmk-sec-toc__chevron" aria-hidden />
      </Button>
      {open ? (
        <Button
          type="button"
          variant="ghost"
          className="zmk-sec-toc__backdrop h-auto min-w-0 p-0"
          aria-label="Close contents"
          onClick={() => setOpen(false)}
        />
      ) : null}
      <nav id={listId} className="zmk-sec-toc__list">
        {tocSections.map((section) => (
          <a
            href={`#${section.id}`}
            key={section.id}
            className={cn(activeId === section.id && "is-active")}
            aria-current={activeId === section.id ? "location" : undefined}
            onClick={(event) => {
              const node = document.getElementById(section.id);
              setOpen(false);
              if (!node) return;
              event.preventDefault();
              const top =
                node.getBoundingClientRect().top +
                window.scrollY -
                mobileTocOffset();
              window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
              window.history.replaceState(null, "", `#${section.id}`);
            }}
          >
            {section.label}
          </a>
        ))}
      </nav>
    </aside>
  );
}

function SecurityFooter() {
  return (
    <footer className="zmk-sec-footer">
      <div className="zmk-sec-footer__inner">
        <div className="zmk-sec-footer__grid">
          <div className="zmk-sec-footer__brand">
            <Image
              src="/images/zom-marketing/logo-white-pictorial.png"
              alt="Zom"
              width={64}
              height={64}
            />
            <p>Operating intelligence for independent RIAs.</p>
          </div>
          <div className="zmk-sec-footer__links">
            <div className="zmk-sec-footer__col">
              <h3>About</h3>
              <Link href="/security">Security</Link>
              <Link href="/about">About</Link>
            </div>
            <div className="zmk-sec-footer__col">
              <h3>Legal</h3>
              <Link href="/privacy">Privacy</Link>
              <Link href="/terms">Terms</Link>
              <Button asChild variant="ghost">
                <RequestAccessLink source="security_footer">
                  Request access
                </RequestAccessLink>
              </Button>
            </div>
          </div>
        </div>
        <div className="zmk-sec-footer__bottom">
          <span>© 2026 Zom. All rights reserved.</span>
        </div>
      </div>
    </footer>
  );
}

export default function SecurityPage() {
  useRevealEffects();
  const { scrolled, hidden, setHidden } = useMarketingNavScroll();

  return (
    <div className={cn("zmk-security-root", hidden && "is-nav-hidden")}>
      <style dangerouslySetInnerHTML={{ __html: securityPageStyles }} />
      <NoScriptReveal />
      <SecurityScrollProgress />
      <LandingRequestDemoModal />
      <SecurityNav scrolled={scrolled} hidden={hidden} setHidden={setHidden} />
      <main className="zmk-sec-main">
        <HeroSection />
        <div className="zmk-sec-layout">
          <SecurityToc />
          <div className="zmk-sec-content">
            <OverviewSection />
            {securityDomainSections.map((section) => (
              <SecurityDomainSection key={section.key} section={section} />
            ))}
            <GovernanceSection />
            <RegulatorySection />
            <VdpSection />
            <FaqSection />
            <ClosingNote />
          </div>
        </div>
      </main>
      <SecurityFooter />
    </div>
  );
}
